import styled from '@emotion/styled/macro';
import { BaseButton, BaseSelect, DataTableStyles, Variables, forPhoneOnly } from '@enter/core';
import Icon from '@mdi/react';

export const TitleRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 0;
    min-height: 32px;
    width: 100%;
`;

export const HeaderExtras = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex: none;
    min-height: 32px;
`;

export const IntegrationWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding-left: 16px;
    border-left: 1px solid ${Variables.ThemeLight.Border1};
    min-height: 32px;
`;

export const IntegrationLabel = styled.span`
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    color: ${Variables.ThemeLight.TextMedium};
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

/** Compact shell matching Chris: border + hub icon + borderless BaseSelect inside. */
export const IntegrationSelectShell = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    border: 1px solid ${Variables.ThemeLight.Border1};
    background: ${Variables.ThemeLight.Background2};
    border-radius: 6px;
    height: 32px;
    padding: 0 8px 0 10px;
    min-width: 220px;
`;

export const IntegrationSelectIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${Variables.ThemeLight.TextMild};
    flex: none;

    svg {
        fill: currentColor;
    }
`;

export const IntegrationSelect = styled(BaseSelect)`
    flex: 1;
    min-width: 0;

    .select__control {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        min-height: 30px;
        height: 30px;
    }

    .select__control--is-focused {
        border: none !important;
        box-shadow: none !important;
    }

    .select__value-container {
        margin: 0;
        padding: 0;
    }

    .select__single-value {
        font-size: 13px;
        font-weight: 500;
        color: ${Variables.ThemeLight.TextStrong};
    }

    .select__indicators {
        height: 30px;
    }

    .select__dropdown-indicator {
        padding: 0 2px;
    }
`;

export const IntegrationStatic = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const KpiGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    margin: 16px 0;
    align-items: stretch;

    ${forPhoneOnly(`
        grid-template-columns: 1fr 1fr;
    `)};
`;

export const KpiCard = styled.div`
    background: ${Variables.ThemeLight.Background3};
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 10px;
    padding: 15px 18px;
    min-height: 108px;
    box-sizing: border-box;
    box-shadow: 0 0 0 1px ${Variables.Shadows.small};
`;

export const KpiLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 11px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextMedium};
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

export const KpiValue = styled.div`
    margin-top: 10px;
    font-size: 27px;
    font-weight: 600;
    line-height: 1.1;
    min-height: 30px;
    color: ${Variables.ThemeLight.TextStrong};
    font-variant-numeric: tabular-nums;
`;

export const KpiSub = styled.div`
    margin-top: 3px;
    font-size: 12px;
    line-height: 1.3;
    min-height: 16px;
    color: ${Variables.ThemeLight.TextMild};
`;

export const KpiSubWarn = styled(KpiSub)`
    color: ${Variables.ThemeLight.AttentionInverted3};
`;

export const TabsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    row-gap: 8px;
    align-items: center;
    gap: 8px;
    padding: 20px 0px 10px;
`;

export const TabToolbar = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    flex: none;
`;

export const WorkerConfigButton = styled(BaseButton)`
    && {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        overflow: hidden;
        white-space: nowrap;
        padding: 0 12px;
        width: max-content;
        min-width: max-content;
        flex-shrink: 0;
    }
`;

/** Chris table card: 10px radius, clip inner table. */
export const TableCardWrap = styled.div`
    width: 100%;

    & > * {
        border-radius: 10px;
        overflow: hidden;
    }
`;

export const TableShell = styled.div`
    width: 100%;

    ${DataTableStyles.TableHeader} {
        padding: 9px 14px;
        font-size: 11px;
        font-weight: 600;
        line-height: 1;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        text-align: left;
        color: ${Variables.ThemeLight.TextMedium};
        background: ${Variables.ThemeLight.Background1};
        border-bottom: 1px solid ${Variables.ThemeLight.Border1};
        border-right: none;
    }

    ${DataTableStyles.TableHead} {
        background: ${Variables.ThemeLight.Background1};
    }

    ${DataTableStyles.TableCell} {
        text-align: left;
        justify-content: flex-start;
    }

    /* Action column: Chris row controls must not wrap or clip inside DataTable cells. */
    ${DataTableStyles.TableCell}:last-of-type {
        overflow: visible;
        word-break: normal;
        justify-content: flex-end;
    }

    ${DataTableStyles.NavigationContainer} {
        gap: 12px;
    }
`;

export const HeaderSearch = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    flex: none;
`;

export const SearchInputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 248px;
    height: 36px;
    padding: 0 10px;
    border: 1px solid ${Variables.ThemeLight.Border1};
    background: ${Variables.ThemeLight.Background3};
    border-radius: 6px;

    & > div {
        flex: 1;
        min-width: 0;
        width: 100%;
    }

    input {
        height: 34px;
        padding-left: 26px;
        border: none;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 400;
        color: ${Variables.ThemeLight.TextStrong};
        background: transparent;

        &::placeholder {
            color: ${Variables.ThemeLight.TextXMild};
            font-size: 14px;
        }

        &:hover {
            background: transparent;
            border-color: transparent;
        }

        &:focus,
        &:active {
            background: transparent;
            border: none;
            box-shadow: none;
        }
    }
`;

export const SearchIcon = styled(Icon)`
    position: absolute;
    left: 10px;
    width: 18px;
    height: 18px;
    fill: ${Variables.ThemeLight.TextMild};
    pointer-events: none;
    z-index: 1;
`;

export const TableHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 13px 16px;
    border-bottom: 1px solid ${Variables.ThemeLight.Border1};
    flex-wrap: wrap;
`;

export const TableTitle = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const TableSub = styled.div`
    font-size: 13px;
    color: ${Variables.ThemeLight.TextMild};
`;

export const TableHeaderActions = styled.div`
    margin-left: auto;
    display: flex;
    gap: 8px;
    align-items: center;
`;

export const NightlyHint = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 6px;
    padding: 0 12px;
    height: 32px;
    background: ${Variables.ThemeLight.Background1};
    color: ${Variables.ThemeLight.TextMedium};
`;

export const Cell = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    flex-grow: 1;
    padding: 8px 0;
    text-align: left;
    width: 100%;
`;

/** Action column: Chris inline-flex row, end-aligned. */
export const ActionCell = styled(Cell)`
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    word-break: normal;
`;

export const CellPrimary = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const CellSecondary = styled.div`
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMild};
    margin-top: 1px;
    font-variant-numeric: tabular-nums;
`;

export const PatientNameLink = styled(BaseButton)`
    && {
        min-width: 0;
        height: auto;
        padding: 0;
        justify-content: flex-start;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.3;
        color: ${Variables.ThemeLight.TextStrong};
        background: transparent;
        border: none;
        box-shadow: none;
    }

    &&:hover:not(:disabled) {
        color: ${Variables.ThemeLight.PrimaryConstant6};
        text-decoration: underline;
        background: transparent;
    }
`;

export const BalanceDueValue = styled.div<{ $muted?: boolean; $credit?: boolean }>`
    font-size: 14px;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    color: ${p => {
        if (p.$credit) {
            return Variables.ThemeLight.AttentionInverted3;
        }
        if (p.$muted) {
            return Variables.ThemeLight.TextXMild;
        }
        return Variables.ThemeLight.TextStrong;
    }};
`;

export const BalanceTag = styled.span`
    margin-top: 3px;
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.2;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    white-space: normal;
    color: ${Variables.ThemeLight.AttentionInverted3};
    background: ${Variables.ThemeLight.AttentionMild3};
    border: 1px solid ${Variables.ThemeLight.AttentionMild1};
    border-radius: 4px;
    padding: 3px 5px;
`;

export const MutedAmount = styled.span`
    font-size: 14px;
    color: ${Variables.ThemeLight.TextMild};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

export const TabCountPill = styled.span<{ $kind: 'needs' | 'paid' | 'neutral' }>`
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    font-size: 11px;
    line-height: 1;
    border-radius: 10px;
    padding: 3px 7px;
    background: ${p => {
        if (p.$kind === 'needs') {
            return Variables.ThemeLight.AttentionMild3;
        }
        if (p.$kind === 'paid') {
            return Variables.ThemeLight.SuccessMild3;
        }
        return Variables.ThemeLight.Background1;
    }};
    color: ${p => {
        if (p.$kind === 'needs') {
            return Variables.ThemeLight.AttentionInverted3;
        }
        if (p.$kind === 'paid') {
            return Variables.ThemeLight.SuccessInverted3;
        }
        return Variables.ThemeLight.TextMedium;
    }};
`;

export const TableEmpty = styled.div`
    padding: 54px 24px;
    text-align: center;
`;

export const TableEmptyMessage = styled.div`
    font-size: 14px;
    font-weight: 400;
    color: ${Variables.ThemeLight.TextMild};
`;

export const ConfirmedChip = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 500;
    color: ${Variables.ThemeLight.PrimaryInverted3};
    background: ${Variables.ThemeLight.PrimaryMild2};
    border: 1px solid ${Variables.ThemeLight.PrimaryMild1};
    border-radius: 4px;
    padding: 3px 7px;
`;

export const DaysChip = styled.span<{ $tone: 'ok' | 'warn' | 'bad' }>`
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 600;
    border-radius: 4px;
    padding: 3px 8px;
    font-variant-numeric: tabular-nums;
    background: ${p => {
        if (p.$tone === 'bad') return Variables.ThemeLight.WarningMild3;
        if (p.$tone === 'warn') return Variables.ThemeLight.AttentionMild3;
        return Variables.ThemeLight.SuccessMild3;
    }};
    color: ${p => {
        if (p.$tone === 'bad') return Variables.ThemeLight.WarningInverted3;
        if (p.$tone === 'warn') return Variables.ThemeLight.AttentionInverted3;
        return Variables.ThemeLight.SuccessInverted3;
    }};
`;

export const Unknown = styled.span`
    color: ${Variables.ThemeLight.TextXMild};
`;

/**
 * Needs Input outline control (Set Balance / Apply Payments).
 * Chris: 32px, 13px/500, pad 0 11px, radius 6px; hover primary-x-mild / primary-mild.
 */
export const RowActionButton = styled(BaseButton)`
    && {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: none;
        flex-shrink: 0;
        width: auto;
        min-width: 0;
        height: 32px;
        padding: 0 11px;
        gap: 6px;
        overflow: hidden;
        font-size: 13px;
        font-weight: 500;
        line-height: 1;
        white-space: nowrap;
        border-radius: 6px;
        background: ${Variables.ThemeLight.Background3};
        border: 1px solid ${Variables.ThemeLight.Border1};
        color: ${Variables.ThemeLight.TextStrong};
    }

    && svg {
        width: 16px;
        height: 16px;
        flex: none;
        fill: ${Variables.ThemeLight.PrimaryConstant6};
    }

    && svg ~ span,
    && span + svg {
        margin-left: 0;
    }

    &&:hover:not(:disabled) {
        background: ${Variables.ThemeLight.PrimaryOpacity3};
        border-color: ${Variables.ThemeLight.PrimaryMild2};
    }
`;

export const RowActionGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    justify-content: flex-end;
    flex-wrap: nowrap;
    width: max-content;
    max-width: 100%;
`;

/** Needs Input primary: Confirm Balance. Chris: 600/13, pad 0 11px, hover brighten. */
export const ConfirmBalanceButton = styled(BaseButton)`
    && {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: none;
        flex-shrink: 0;
        width: auto;
        min-width: 0;
        height: 32px;
        padding: 0 11px;
        gap: 6px;
        overflow: hidden;
        font-size: 13px;
        font-weight: 600;
        line-height: 1;
        white-space: nowrap;
        border-radius: 6px;
        border: 1px solid transparent;
        background: ${Variables.ThemeLight.PrimaryConstant6};
        color: ${Variables.Colors.white};
    }

    && svg {
        width: 16px;
        height: 16px;
        flex: none;
        fill: ${Variables.Colors.white};
    }

    && svg ~ span,
    && span + svg {
        margin-left: 0;
    }

    &&:hover:not(:disabled) {
        filter: brightness(1.08);
    }
`;

/**
 * Outstanding / Paid: 32×32 visibility. Chris: white, border, radius 6px,
 * text-medium icon, hover neutral-x-mild.
 */
export const IconActionButton = styled(BaseButton)`
    && {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 32px;
        width: 32px;
        height: 32px;
        padding: 0;
        border-radius: 6px;
        background: ${Variables.ThemeLight.Background3};
        border: 1px solid ${Variables.ThemeLight.Border1};
        color: ${Variables.ThemeLight.TextMedium};
    }

    && svg {
        width: 18px;
        height: 18px;
        fill: ${Variables.ThemeLight.TextMedium};
    }

    &&:hover:not(:disabled) {
        background: ${Variables.ThemeLight.Background1};
        border-color: ${Variables.ThemeLight.Border1};
        color: ${Variables.ThemeLight.TextMedium};
    }
`;

/** Shown when collection invoice integrations API returns an empty list. */
export const NotConfiguredBanner = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin: 8px 0 24px;
    padding: 28px 32px;
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 10px;
    background: ${Variables.ThemeLight.Background3};
`;

export const NotConfiguredTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
`;
